import React, { createContext, useContext, useState, useEffect } from 'react';
import { getPriceForCartons, getTierForCartons, getAvailableCartons, getVariantString } from '../utils/pricing';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    // Load cart from localStorage on initial render
    const savedCart = localStorage.getItem('magomeal_cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('magomeal_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product) => {
    setCartItems(prevItems => {
      // For tiered products, check if same product already exists
      const existingItem = prevItems.find(
        item => item.id === product.id
      );
      
      if (existingItem) {
        // Replace the existing item with the new selection (different tier)
        return prevItems.map(item =>
          item.id === product.id
            ? { 
                ...product, 
                cartItemId: item.cartItemId,
                cartons: product.cartons || 1
              }
            : item
        );
      }
      
      return [...prevItems, { 
        ...product, 
        cartItemId: Date.now(),
        cartons: product.cartons || 1
      }];
    });
    
    // Open cart drawer when item is added
    setIsCartOpen(true);
  };

  const removeFromCart = (cartItemId) => {
    setCartItems(prevItems => prevItems.filter(item => item.cartItemId !== cartItemId));
  };

  // Update cartons (quantity) for tiered pricing
  const updateCartons = (cartItemId, newCartons) => {
    setCartItems(prevItems =>
      prevItems.map(item => {
        if (item.cartItemId !== cartItemId) return item;
        
        // Get available carton options for this product
        const availableCartons = getAvailableCartons(item.id);
        
        // Find the closest valid carton count
        if (!availableCartons.includes(newCartons)) {
          // Find nearest available option
          const currentIndex = availableCartons.indexOf(item.cartons);
          if (newCartons > item.cartons && currentIndex < availableCartons.length - 1) {
            newCartons = availableCartons[currentIndex + 1];
          } else if (newCartons < item.cartons && currentIndex > 0) {
            newCartons = availableCartons[currentIndex - 1];
          } else {
            return item; // No change possible
          }
        }
        
        // Get the new pricing tier
        const tier = getTierForCartons(item.id, newCartons);
        if (!tier) return item;
        
        return {
          ...item,
          cartons: newCartons,
          price: tier.price,
          variant: getVariantString(item.id, newCartons),
          quantity: 1 // Always 1 for tiered pricing
        };
      })
    );
  };

  // Legacy updateQuantity - now redirects to updateCartons for tiered products
  const updateQuantity = (cartItemId, newQuantity) => {
    const item = cartItems.find(i => i.cartItemId === cartItemId);
    if (!item) return;
    
    // For tiered products, adjust cartons instead
    if (item.id === 'edible-cup-110' || item.id === 'edible-cup-220') {
      const availableCartons = getAvailableCartons(item.id);
      const currentIndex = availableCartons.indexOf(item.cartons);
      
      if (newQuantity > item.quantity && currentIndex < availableCartons.length - 1) {
        // Increase to next tier
        updateCartons(cartItemId, availableCartons[currentIndex + 1]);
      } else if (newQuantity < item.quantity && currentIndex > 0) {
        // Decrease to previous tier
        updateCartons(cartItemId, availableCartons[currentIndex - 1]);
      }
      return;
    }
    
    // For non-tiered products
    if (newQuantity < 1) {
      removeFromCart(cartItemId);
      return;
    }
    
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.cartItemId === cartItemId
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      // For tiered products, use the tier price directly
      if (item.id === 'edible-cup-110' || item.id === 'edible-cup-220') {
        const tierPrice = getPriceForCartons(item.id, item.cartons);
        return total + tierPrice;
      }
      // For other products, use price * quantity
      return total + (item.price * item.quantity);
    }, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => {
      // For tiered products, count cartons
      if (item.id === 'edible-cup-110' || item.id === 'edible-cup-220') {
        return count + (item.cartons || 1);
      }
      return count + item.quantity;
    }, 0);
  };

  // Get the actual price for a cart item (considering tiered pricing)
  const getItemPrice = (item) => {
    if (item.id === 'edible-cup-110' || item.id === 'edible-cup-220') {
      return getPriceForCartons(item.id, item.cartons);
    }
    return item.price * item.quantity;
  };

  // Get item details including tier info
  const getItemDetails = (item) => {
    if (item.id === 'edible-cup-110' || item.id === 'edible-cup-220') {
      const tier = getTierForCartons(item.id, item.cartons);
      return {
        ...item,
        price: tier?.price || item.price,
        variant: getVariantString(item.id, item.cartons),
        cups: tier?.cups || 0,
        cartons: item.cartons
      };
    }
    return item;
  };

  // Check if can increase cartons
  const canIncreaseCartons = (cartItemId) => {
    const item = cartItems.find(i => i.cartItemId === cartItemId);
    if (!item) return false;
    
    const availableCartons = getAvailableCartons(item.id);
    const currentIndex = availableCartons.indexOf(item.cartons);
    return currentIndex < availableCartons.length - 1;
  };

  // Check if can decrease cartons
  const canDecreaseCartons = (cartItemId) => {
    const item = cartItems.find(i => i.cartItemId === cartItemId);
    if (!item) return false;
    
    const availableCartons = getAvailableCartons(item.id);
    const currentIndex = availableCartons.indexOf(item.cartons);
    return currentIndex > 0;
  };

  const toggleCart = () => {
    setIsCartOpen(prev => !prev);
  };

  const closeCart = () => {
    setIsCartOpen(false);
  };

  const openCart = () => {
    setIsCartOpen(true);
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      isCartOpen,
      addToCart,
      removeFromCart,
      updateQuantity,
      updateCartons,
      clearCart,
      getCartTotal,
      getCartCount,
      getItemPrice,
      getItemDetails,
      canIncreaseCartons,
      canDecreaseCartons,
      toggleCart,
      closeCart,
      openCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
